import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

// Bootstrap 5.2.3 da bg-*-subtle klasslari YO'Q (5.3 da qo'shilgan) — fon umuman chiqmaydi.
// Shuning uchun pastel ranglarni inline style bilan beramiz (hex qiymatlar = 5.3 dagi subtle ranglari).
const PASTEL_GREEN = { background: 'var(--pastel-green-bg, #d1e7dd)', color: 'var(--pastel-green-ink, #0f5132)' }
const PASTEL_RED = { background: 'var(--pastel-red-bg, #f8d7da)', color: 'var(--pastel-red-ink, #842029)' }
const PASTEL_NEUTRAL = { background: 'var(--pastel-neu-bg, #f8f9fa)', color: 'var(--pastel-neu-ink, #6c757d)', border: '1px solid var(--pastel-neu-bd, #dee2e6)' }
const SOLID_RED = { background: '#dc3545', color: '#ffffff' }

// Narx tarixi grafigi (SVG) — kelish narxlari ($/tonna) vaqt bo'yicha.
// Ko'p qabul bo'lsa yorliqlar ustma-ust tushmasligi uchun: y-tarmoq (min/o'rta/max),
// faqat eng past/yuqori/oxirgi nuqta belgilanadi, x-o'qda ~6 ta sana.
function PriceChart({ rows }) {
	if (!rows || rows.length < 2) {
		return <div className="text-muted text-center py-3" style={{ fontSize: '13px' }}>Grafik uchun kamida 2 ta qabul kerak</div>
	}
	const prices = rows.map(r => Number(r.price_t))
	const n = prices.length
	const w = 560, h = 175, padL = 46, padR = 14, padT = 16, padB = 24
	const min = Math.min(...prices), max = Math.max(...prices), rng = (max - min) || 1
	const up = prices[n - 1] >= prices[0]
	const col = up ? '#0f9d6b' : '#e5484d'
	const X = i => padL + i / (n - 1) * (w - padL - padR)
	const Y = v => padT + (1 - (v - min) / rng) * (h - padT - padB)
	const line = prices.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ')
	const area = `${line} ${X(n - 1).toFixed(1)},${h - padB} ${X(0).toFixed(1)},${h - padB}`
	const yTicks = [max, (min + max) / 2, min]
	const nLab = Math.min(6, n)
	const xIdx = Array.from({ length: nLab }, (_, k) => Math.round(k * (n - 1) / (nLab - 1)))
	const idxMin = prices.indexOf(min), idxMax = prices.indexOf(max)
	return (
		<svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`}>
			{yTicks.map((v, k) => (
				<g key={k}>
					<line x1={padL} y1={Y(v)} x2={w - padR} y2={Y(v)} stroke="#e7e5f1" strokeWidth="1" strokeDasharray="3 3" />
					<text x={padL - 6} y={Y(v) + 3} fontSize="9.5" fill="#8a86a3" textAnchor="end">{Math.round(v)}</text>
				</g>
			))}
			<polygon points={area} fill={col} opacity="0.09" />
			<polyline points={line} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" />
			<circle cx={X(idxMax)} cy={Y(max)} r="3" fill={col} />
			<circle cx={X(idxMin)} cy={Y(min)} r="3" fill={col} />
			<circle cx={X(n - 1)} cy={Y(prices[n - 1])} r="4" fill={col} stroke="#fff" strokeWidth="1.5" />
			<text x={X(n - 1) - 4} y={Y(prices[n - 1]) - 8} fontSize="11.5" fontWeight="700" fill={col} textAnchor="end">{prices[n - 1]}</text>
			{xIdx.map((i, k) => (
				<text key={k} x={X(i)} y={h - 7} fontSize="9.5" fill="#8a86a3"
					textAnchor={k === 0 ? 'start' : (k === xIdx.length - 1 ? 'end' : 'middle')}>
					{rows[i].date ? formatDate(rows[i].date) : ''}
				</text>
			))}
		</svg>
	)
}

function RollPrices() {
	const { t } = useTranslation()

	const [data, setData] = useState({ data: [], kurs: 0, market_index: 0 })
	const [adj, setAdj] = useState(0)
	const [openRolls, setOpenRolls] = useState({})
	const [details, setDetails] = useState({})
	const [sortField, setSortField] = useState('value')
	const [sortDir, setSortDir] = useState('desc')

	async function load() {
		const res = await GET('/report/roll-prices', {}, { loader: true })
		if (httpOk(res)) setData(res.data)
	}
	useEffect(() => { load() }, [])

	async function toggleRoll(id) {
		const isOpen = !!openRolls[id]
		setOpenRolls({ ...openRolls, [id]: !isOpen })
		if (!isOpen && !details[id]) {
			setDetails(d => ({ ...d, [id]: { loading: true, rows: [] } }))
			const res = await GET('/report/roll-price-detail?element_id=' + id, {}, { loader: false })
			if (httpOk(res)) setDetails(d => ({ ...d, [id]: { loading: false, rows: res.data.data || [] } }))
			else setDetails(d => ({ ...d, [id]: { loading: false, rows: [] } }))
		}
	}

	function calc(r) {
		const qolda = (Number(r.avto_t) || 0) + adj
		const gap = qolda - (Number(r.buy_t) || 0)
		const cheap = gap >= 0
		const qt = Number(r.qoldiq_t) || 0
		const win = qt * gap
		const value = qt * qolda
		const rising = (Number(r.avto_t) || 0) > (Number(r.buy_t) || 0)
		let sig, sstyle
		if (rising && cheap && r.days !== null && r.days <= 7) { sig = 'HOZIR OL'; sstyle = SOLID_RED }
		else if (rising && cheap) { sig = 'OL'; sstyle = PASTEL_GREEN }
		else if (!rising && !cheap) { sig = 'KUT'; sstyle = PASTEL_RED }
		else { sig = 'USHLA'; sstyle = PASTEL_NEUTRAL }
		return { qolda, gap, cheap, win, value, sig, sstyle }
	}

	function daysClass(d) { return (d === null || d === undefined) ? '' : (d <= 7 ? 'text-danger' : (d <= 20 ? 'text-success' : '')) }

	function handleSort(field) {
		if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
		else { setSortField(field); setSortDir('desc') }
	}
	function arrow(field) {
		if (sortField !== field) return <i className="uil uil-angle-up-b" style={{ opacity: .25 }}></i>
		return <i className={`uil ${sortDir === 'asc' ? 'uil-angle-up-b' : 'uil-angle-down'}`}></i>
	}
	function sortVal(r, field) {
		const c = calc(r)
		switch (field) {
			case 'name': return (r.element_name || '').toLowerCase()
			case 'qoldiq': return Number(r.qoldiq_t) || 0
			case 'days': return r.days === null ? 999999 : r.days
			case 'buy': return Number(r.buy_t) || 0
			case 'avto': return Number(r.avto_t) || 0
			case 'qolda': return c.qolda
			case 'gap': return c.gap
			case 'win': return c.win
			case 'value': return c.value
			case 'margin': return Number(r.margin) || 0
			default: return 0
		}
	}

	const rows = [...(data.data || [])].sort((a, b) => {
		const av = sortVal(a, sortField), bv = sortVal(b, sortField)
		if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
		return sortDir === 'asc' ? av - bv : bv - av
	})

	// Umumiy
	let totWin = 0, totValue = 0, cheapCnt = 0, expCnt = 0, mSum = 0, tSum = 0
	rows.forEach(r => {
		const c = calc(r)
		totWin += c.win; totValue += c.value
		if (c.cheap) cheapCnt++; else expCnt++
		mSum += (Number(r.margin) || 0) * (Number(r.qoldiq_t) || 0)
		tSum += Number(r.qoldiq_t) || 0
	})
	const avgMargin = tSum ? (mSum / tSum) : 0
	const kurs = Number(data.kurs) || 0

	const Th = ({ field, label, cls }) => (
		<th className={cls || 'text-end'} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort(field)}>
			{label} {arrow(field)}
		</th>
	)

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">{t('roll_prices')} [{rows.length}]</h4>
			</div>

			<div className="card">
				<div className="card-body">
					{/* Umumiy ko'rsatkichlar */}
					<div className="row g-2 mb-3">
						<div className="col-md">
							<div className="border rounded p-2 h-100">
								<div className="text-muted small text-uppercase">{t('stock_value')}</div>
								<div className="fw-bold fs-5">{formatMoney(totValue)} $</div>
								<div className="text-muted small">≈ {formatMoney(totValue * kurs / 1e6, 1)} mln so'm</div>
							</div>
						</div>
						<div className="col-md">
							<div className="border rounded p-2 h-100">
								<div className="text-muted small text-uppercase">{t('current_margin')}</div>
								<div className="fw-bold fs-5 text-primary">{avgMargin.toFixed(1)}%</div>
								<div className="text-muted small">hozir sotyapsiz (lag)</div>
							</div>
						</div>
						<div className="col-md">
							<div className="border rounded p-2 h-100">
								<div className="text-muted small text-uppercase">{t('expected_profit')}</div>
								<div className={`fw-bold fs-5 ${totWin >= 0 ? 'text-success' : 'text-danger'}`}>{totWin >= 0 ? '+' : '−'}{formatMoney(Math.abs(totWin))} $</div>
								<div className="text-muted small">~1-2 hafta · narx ko'tarilganda</div>
							</div>
						</div>
						<div className="col-md">
							<div className="border rounded p-2 h-100">
								<div className="text-muted small text-uppercase">Arzon / Qimmat</div>
								<div className="fw-bold fs-5">{cheapCnt} / {expCnt}</div>
								<div className="text-muted small">bozordan past / yuqori</div>
							</div>
						</div>
						<div className="col-md">
							<div className="border rounded p-2 h-100">
								<div className="text-muted small text-uppercase">{t('market_index')}</div>
								<div className={`fw-bold fs-5 ${data.market_index >= 0 ? 'text-success' : 'text-danger'}`}>
									{data.market_index >= 0 ? '▲' : '▼'} {Math.abs(data.market_index)}%
								</div>
								<div className="text-muted small">kurs: {formatMoney(kurs)}</div>
							</div>
						</div>
					</div>

					{/* Bozor narxini yangilash (global +$) */}
					<div className="d-flex align-items-center gap-3 flex-wrap border rounded p-2 mb-3 bg-light">
						<div>
							<div className="fw-semibold">{t('update_market_price')}</div>
							<div className="text-muted small">Narx oshdi/tushdimi? Har tonnaga qo'shiladigan miqdorni kiriting — <b>barcha rulonlarga</b> qo'llanadi.</div>
						</div>
						<div className="input-group" style={{ width: '180px' }}>
							<button className="btn btn-outline-secondary" onClick={() => setAdj(adj - 5)}>−</button>
							<input type="number" className="form-control text-center fw-bold" value={adj} step="5"
								onChange={e => setAdj(Number(e.target.value) || 0)} />
							<span className="input-group-text">$/t</span>
							<button className="btn btn-outline-secondary" onClick={() => setAdj(adj + 5)}>+</button>
						</div>
						<span className="badge" style={adj >= 0 ? PASTEL_GREEN : PASTEL_RED}>
							{adj >= 0 ? '+' : '−'}{Math.abs(adj)} $/t barcha rulonga
						</span>
						<button className="btn btn-sm btn-outline-dark" onClick={() => setAdj(0)}>0 ga qaytar</button>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ width: '33px' }} className="text-center">#</th>
									<Th field="name" label={t('roll')} cls="text-start" />
									<Th field="qoldiq" label={`Qoldiq · ${t('days_short')}`} />
									<Th field="buy" label={`${t('avg_purchase')} $/t`} />
									<Th field="avto" label={`${t('auto_price')} $/t`} />
									<Th field="qolda" label={`${t('manual_price')} $/t`} />
									<Th field="gap" label={t('status_col')} cls="text-start" />
									<Th field="win" label={t('expected_profit')} />
									<Th field="value" label={`${t('stock_value')} $`} />
									<th className="text-start">{t('supplier')}</th>
									<th className="text-start">Signal</th>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 &&
									<tr><td colSpan={11} className="text-center text-muted py-4">{t('list_empty')}</td></tr>
								}
								{rows.map((r, index) => {
									const c = calc(r)
									const isOpen = !!openRolls[r.element_id]
									const det = details[r.element_id]
									return (
										<React.Fragment key={r.element_id}>
											<tr style={{ cursor: 'pointer' }} onClick={() => toggleRoll(r.element_id)}>
												<td className="text-center">{index + 1}</td>
												<td>
													<i className={`uil ${isOpen ? 'uil-angle-down' : 'uil-angle-right'} me-1`}></i>
													<b>{r.element_name}</b>
												</td>
												<td className="text-end">
													{formatMoney(r.qoldiq_t, 1)} t
													<span className={`ms-1 ${daysClass(r.days)}`} style={{ fontSize: '12px' }}>
														· {r.days !== null ? `${r.days} ${t('days_short')}` : '—'}
													</span>
												</td>
												<td className="text-end">{formatMoney(r.buy_t)}</td>
												<td className="text-end text-muted">{formatMoney(r.avto_t)}</td>
												<td className="text-end fw-bold text-primary">{formatMoney(c.qolda)}</td>
												<td>
													<span className="badge" style={c.cheap ? PASTEL_GREEN : PASTEL_RED}>
														{c.cheap ? t('cheap_status') : t('expensive_status')}
													</span>
													<span className="text-muted ms-1" style={{ fontSize: '12px' }}>{c.gap >= 0 ? '+' : '−'}{formatMoney(Math.abs(c.gap))}</span>
												</td>
												<td className={`text-end fw-bold ${c.win >= 0 ? 'text-success' : 'text-danger'}`}>
													{c.win >= 0 ? '+' : '−'}{formatMoney(Math.abs(c.win))} $
												</td>
												<td className="text-end">{formatMoney(c.value)} $</td>
												<td style={{ fontSize: '12.5px' }}>
													{r.supplier_name || '—'}
													{r.supplier_price ? <span className="text-muted"> · {formatMoney(r.supplier_price)}$</span> : null}
												</td>
												<td><span className="badge" style={c.sstyle}>{c.sig}</span></td>
											</tr>

											{isOpen &&
												<tr className="bg-light">
													<td colSpan={11} className="p-0">
														<div className="p-3">
															{det && det.loading && <div className="text-center text-muted py-2">...</div>}
															{det && !det.loading &&
																<div className="row g-3">
																	<div className="col-lg-7">
																		<div className="border rounded bg-white p-2 h-100">
																			<div className="text-muted small text-uppercase mb-1">{t('price_history')} — $/tonna</div>
																			<PriceChart rows={det.rows} />
																		</div>
																	</div>
																	<div className="col-lg-5">
																		<div className="border rounded bg-white p-2">
																			<div className="text-muted small text-uppercase mb-1">{t('arrival_history')} — qachon, qancha, qanday narxda</div>
																			<div className="table-responsive" style={{ maxHeight: '260px', overflowY: 'auto' }}>
																				<table className="table table-sm mb-0">
																					<thead>
																						<tr>
																							<th>{t('date')}</th>
																							<th className="text-end">{t('tonna')}</th>
																							<th className="text-end">{t('metr')}</th>
																							<th className="text-end">$/t</th>
																							<th>{t('supplier')}</th>
																						</tr>
																					</thead>
																					<tbody>
																						{det.rows.length === 0 &&
																							<tr><td colSpan={5} className="text-center text-muted">{t('list_empty')}</td></tr>
																						}
																						{det.rows.map((x, i) => (
																							<tr key={i}>
																								<td>{formatDate(x.date)}</td>
																								<td className="text-end">{formatMoney(x.tonna, 2)}</td>
																								<td className="text-end">{formatMoney(x.metr)}</td>
																								<td className="text-end fw-semibold">{formatMoney(x.price_t)}</td>
																								<td style={{ fontSize: '12px' }}>{x.supplier || '—'}</td>
																							</tr>
																						))}
																					</tbody>
																					{det.rows.length > 0 &&
																						<tfoot>
																							<tr className="fw-bold">
																								<td>Jami</td>
																								<td className="text-end">{formatMoney(det.rows.reduce((s, x) => s + Number(x.tonna || 0), 0), 2)}</td>
																								<td className="text-end">{formatMoney(det.rows.reduce((s, x) => s + Number(x.metr || 0), 0))}</td>
																								<td></td><td></td>
																							</tr>
																						</tfoot>
																					}
																				</table>
																			</div>
																		</div>
																	</div>
																</div>
															}
														</div>
													</td>
												</tr>
											}
										</React.Fragment>
									)
								})}
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td><td></td><td></td><td></td><td></td>
									<td className={`text-end ${totWin >= 0 ? 'text-success' : 'text-danger'}`}><b>{totWin >= 0 ? '+' : '−'}{formatMoney(Math.abs(totWin))} $</b></td>
									<td className="text-end"><b>{formatMoney(totValue)} $</b></td>
									<td></td><td></td>
								</tr>
							</tfoot>
						</table>
					</div>

					<div className="text-muted mt-2" style={{ fontSize: '12px' }}>
						<b>Avto narx</b> — oxirgi qabul narxi (tizim oladi) · <b>Qo'lda narx</b> = Avto + yuqoridagi o'zgarish.
						<b> Kutilayotgan foyda</b> = Qoldiq(t) × (Qo'lda narx − O'rt.xarid) — narx ko'tarilib, eski stok tugagach (~1-2 hafta) olasiz.
						<b> Hozirgi marja</b> — real chek narxidan (lag davri, yupqa).
						Rulonni bosing → narx tarixi grafigi + kelish tarixi (butun tarix).
					</div>
				</div>
			</div>
		</>
	)
}

export default RollPrices
